import { ChangeEvent, useState } from "react";
import { send } from "src/utils/fetch";
import goto from "src/pageDefinitions/goto";
import {
  getInAppAutoCookie,
  setInAppAutoCookie,
} from "src/utils/authCookieParser";
import {
  saveMainSurveyAnswers,
  saveSurveyAnswers,
  setPurchaseSurveyAnswers,
  setHRTSurveyAnswers,
} from "src/utils/redux/slices/userData";
import getStore from "src/utils/redux/store";
import { useCallbackRef } from "src/hooks/lifecycle";
import { SelectedMedPlanType } from "src/utils/medPlanTypes";

import {
  getPayloadForGlp1CompanionUser,
  getPayloadForMedUser,
  getPayloadForMhtUser,
  getPayloadForHRTCheckout,
} from "./purchaseUtils";
import {
  isB2BUpsellClinicalRoute,
  isFreemiumClinicalRoute,
} from "@utils/userSegment";
import { routeConstants } from "@utils/constants";
import {
  BrowserSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import { setRecommendationContext } from "src/utils/redux/slices/recommendationContext";
import { setRecommendedPlanById } from "src/utils/redux/slices/recommendedPlan";
import { MED_FLOW, MED_SKU } from "src/utils/medEligibility";
import { getMedPlanIdBySKU } from "src/utils/plans";
import { medSkuEntryPoints } from "@pageDefinitions/goto/paymentResults";

const isMedPlanType = (planType: string) => {
  return [
    "compounded",
    "branded",
    "oralsMetformin",
    "genericLiraglutide",
    "compoundedMicrodose",
    "compoundedMicrodoseProactiveHealthBiomarker",
  ].includes(planType);
};

const getMedSkuFromPlanType = (planType: string): MED_SKU => {
  switch (planType) {
    case "compounded":
      return MED_SKU.COMPOUNDED;
    case "branded":
      return MED_SKU.BRANDED;
    case "oralsMetformin":
      return MED_SKU.ORALS_METFORMIN;
    case "genericLiraglutide":
      return MED_SKU.GENERIC_LIRAGLUTIDE;
    case "compoundedMicrodose":
      return MED_SKU.COMPOUNDED_MICRODOSE;
    case "compoundedMicrodoseProactiveHealthBiomarker":
      return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
    case "cream":
      return MED_SKU.HRT_CREAM;
    case "patch":
      return MED_SKU.HRT_PATCH;
    default:
      return MED_SKU.BRANDED; // fallback
  }
};

export const redirectToCounterOffer = ({ subscriptionId }) => {
  let counterOfferRedirectURL = `${window.location.origin}/cancel?fcc=US&subscription_id=BMS_${subscriptionId}`;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("sha")) {
    counterOfferRedirectURL += `&sha=${urlParams.get("sha")}`;
  }

  window.location.href = counterOfferRedirectURL;
};

const prepareMedDataAndUrl = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const isMed = isMedPlanType(planType);

  updateSessionState("browser", {
    selectedMedPlanType: undefined,
    selectedHRTPlanType: undefined,
  });

  if (planType === "genericLiraglutide") {
    urlParams.set("enableLiraglutideMedPlans", "true");
    urlParams.set("liraglutideMedPlansRolloutEnabled", "true");
  }

  if (isMed) {
    const data = glp1Companion
      ? getPayloadForGlp1CompanionUser(email)
      : getPayloadForMedUser(email, planType);

    await getStore().dispatch(
      saveSurveyAnswers(data.surveyAnswers, data.params)
    );
    await getStore().dispatch(
      setPurchaseSurveyAnswers(data.paymentSurveyAnswers)
    );
    if (!glp1Companion) {
      let routeVal: string;
      if (isB2BUpsellClinicalRoute()) {
        routeVal = routeConstants.b2bUpsellClinical;
      } else if (isFreemiumClinicalRoute()) {
        routeVal = routeConstants.freemiumClinical;
      } else {
        routeVal = routeConstants.clinical;
      }

      urlParams.set("route", routeVal);
    }
  } else {
    urlParams.set("standaloneHRTBuyflowEnabledOverwrite", "true");
    urlParams.set("route", routeConstants.hrt);
    await setHRTPreCheckoutData({ email });
  }

  urlParams.set("fcc", "US");
  if (planType === "compoundedMicrodose") {
    urlParams.set("fsub", "OH");
  } else {
    urlParams.set("fsub", "AR");
  }
  if (isEmailUser) {
    urlParams.set("utm_source", "MedDrip");
  }

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);
};

export const redirectToTelehealth = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
}) => {
  await prepareMedDataAndUrl({
    email,
    isEmailUser,
    glp1Companion,
    planType,
  });

  const isMed = isMedPlanType(planType);

  if (isMed) {
    updateSessionState("browser", {
      selectedMedPlanType: planType as SelectedMedPlanType,
    });
  } else {
    updateSessionState("browser", {
      selectedHRTPlanType:
        planType as BrowserSessionState["selectedHRTPlanType"],
    });
  }

  goto.checkout("option_n_20221201_Ex1084var1", true);
};

export const redirectToMedPlanReveal = async ({
  email,
  isEmailUser,
  glp1Companion,
  planType = "branded",
}: {
  email: string;
  isEmailUser?: boolean;
  glp1Companion?: boolean;
  planType?: SelectedMedPlanType | BrowserSessionState["selectedHRTPlanType"];
}) => {
  const isMed = isMedPlanType(planType);
  const medSku = getMedSkuFromPlanType(planType);
  const noomPlanId = getMedPlanIdBySKU(medSku);

  await prepareMedDataAndUrl({
    email,
    isEmailUser,
    glp1Companion,
    planType,
  });

  await getStore().dispatch(
    setRecommendedPlanById(noomPlanId, {
      overwriteForced: true,
      overwriteReset: true,
    })
  );

  const flow = isMed ? MED_FLOW.CLINICAL : MED_FLOW.HRT;

  await getStore().dispatch(
    setRecommendationContext({
      recommendedMedTier: medSku,
      eligibleMedTiers: [medSku],
      flow,
      recommendationId: undefined,
    })
  );

  const entryPoint = medSkuEntryPoints[medSku];
  const flowName = isMed ? "clinical" : "hrt";

  goto.layer("payment-results", null, false, flowName, entryPoint);
};

export async function setHRTPreCheckoutData({ email }: { email: string }) {
  const data = getPayloadForMhtUser(email);
  await getStore().dispatch(
    saveMainSurveyAnswers(data.personaSurveyAnswers, {
      surveyNameSpace: "personaSurveyUS",
    })
  );

  await getStore().dispatch(
    setPurchaseSurveyAnswers(data.paymentSurveyAnswers)
  );

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("fcc", "US");
  urlParams.set("fsub", "AR");
  urlParams.set("idm", "true");
  urlParams.set("MhtEnabledOverwrite", "true");
  urlParams.set("enableMhtFlow", "true");

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);
}

export const redirectToNWCheckoutWithMHTUser = async ({
  email,
  selectedNoomPlanId,
}: {
  email: string;
  selectedNoomPlanId: string;
}) => {
  await setHRTPreCheckoutData({ email });

  goto.checkout(selectedNoomPlanId);
};

export const redirectToHRTCheckoutPage = async ({
  selectedNoomPlanId,
  planType = "cream",
}: {
  planType?: "cream" | "patch";
  selectedNoomPlanId: string;
}) => {
  const { hrtSurveyAnswers } = getPayloadForHRTCheckout({ planType });

  await getStore().dispatch(setHRTSurveyAnswers(hrtSurveyAnswers));

  const creamRequested = planType === "cream";

  const urlParams = new URLSearchParams(window.location.search);
  urlParams.set("selectHRTCreamPlan", creamRequested.toString());

  window.history.pushState({}, "", `${window.location.origin}?${urlParams}`);

  goto.hrtCheckout(selectedNoomPlanId);
};

const callCoachServerOauth = async (requestData) => {
  const CS_STAGING_OAUTH_URL =
    "https://data-test.noom.com/servlets/oauth/token";
  const requestBody = `${encodeURIComponent("grant_type")}=${encodeURIComponent(
    "password"
  )}&${encodeURIComponent("client_id")}=${encodeURIComponent(
    "noom-coach"
  )}&${encodeURIComponent("request")}=${encodeURIComponent(requestData)}`;

  const requestInit: RequestInit = {
    method: "POST",
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
    body: requestBody,
  };
  return fetch(CS_STAGING_OAUTH_URL, requestInit).then(
    async (response) => {
      const text = await response.text();
      let data: JsonObject | string = text;

      try {
        data = JSON.parse(data);
      } catch (e) {
        /* NOP */
      }

      if (response.ok) {
        await setInAppAutoCookie(
          data.accessCode,
          data.access_token,
          data.accountEmail,
          false
        );
        return data;
      }

      throw new Error("Failed response");
    },
    (err) => {
      throw new Error(err);
    }
  );
};

export const createNewUser = async (email, password) => {
  const payload = {
    action: "create_account",
    email,
    userProfileData: {
      email,
      name: "Noom User",
      password,
      phoneNumber: "1234567890",
      language: "en",
      gender: "FEMALE",
      heightInCm: 180,
      weightInKg: 70,
      age: 30,
      isNursing: false,
    },
    password,
  };

  return send("POST", "/api/enrollment/v2/create_account/", payload);
};

export const authenticateUser = async (email, password) => {
  const requestData = `{"mode":"LOGIN","type":"EMAIL_AND_PASSWORD","principal":"${email}","credentials":"${password}"}`;
  return callCoachServerOauth(requestData);
};

export const getInAppUrl = async (
  email: string,
  accessCode: string,
  accessToken: string,
  countryCode: string,
  isAndroid = false,
  isAppEmail = false,
  campaignName = "",
  route = ""
) => {
  function getUrlEndpointRequest() {
    const headers = {
      Authorization: `bearer ${accessToken}`,
    };

    if (isAppEmail) {
      return {
        path: "/internal/sendAppEmail/",
        headers,
        body: {
          access_code: accessCode,
          country: countryCode,
          email,
        },
      };
    }
    return {
      path: "/internal/v2/app/",
      headers,
      body: {
        user_data: {
          gender: "female",
          email,
          country_code: countryCode,
          access_code: accessCode,
          name: "SM Test",
        },
        route_data: {
          trial_duration_days: 14,
          allow_purchase: isAndroid ? "true" : "false",
          sp: "sp",
          utm_source: "appstore",
          fcc: "en",
          ...(campaignName.length > 0 ? { campaign_name: campaignName } : {}),
        },
      },
    };
  }

  const { path, headers, body } = getUrlEndpointRequest();

  return send("POST", path, body, { headers }).then((res) => {
    // Add protocol if missing
    const baseUrl = res.redirect_url || res.url;
    const protocol = baseUrl.includes("http") ? "" : "http://";
    const url = new URL(protocol + baseUrl);

    // On local env the domain/port of the redirect URL needs to be updated to stay on local env
    if (window.location.origin.includes("localhost")) {
      url.protocol = "http";
      url.hostname = "localhost";
      url.port = "8080";
    }

    // Add route as query parameter if provided
    if (route) {
      url.searchParams.set("route", route);
    }

    return url.href;
  });
};

export const useGoToInApp = (
  setStatus: (status: string) => void,
  email: string,
  password: string
) => {
  const [isAndroid, setIsAndroid] = useState(false);
  const toggleIsAndroid = () => setIsAndroid(!isAndroid);

  const [appEmailUrl, setAppEmailUrl] = useState("");

  const [countryCode, setCountryCode] = useState("US");
  const onCountryCodeChange = (e: ChangeEvent<HTMLInputElement>) =>
    setCountryCode(e.target.value);

  async function createInAppUrl(appEmail, campaignName = "", route = "") {
    const autologinCookie = getInAppAutoCookie();

    let user = {
      accountEmail: autologinCookie?.email,
      accessCode: autologinCookie?.accessCode,
      access_token: autologinCookie?.accessToken,
    };

    if (!autologinCookie) {
      setStatus("Creating new user...");
      await createNewUser(email, password);
      user = await authenticateUser(email, password);
    }

    setStatus("Fetching in-app URL from growth...");

    return getInAppUrl(
      user.accountEmail,
      user.accessCode,
      user.access_token,
      countryCode,
      isAndroid,
      appEmail,
      campaignName,
      route
    );
  }

  async function goToInApp() {
    try {
      const url = await createInAppUrl(false);
      setStatus("Redirecting to in-app...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function goToAppFree() {
    try {
      const url = await createInAppUrl(false, "freemium_test_campaign");
      setStatus("Redirecting to app-free route...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function goToFreemium() {
    try {
      const url = await createInAppUrl(false, "", "app-free-onboarding");
      setStatus("Redirecting to freemium route...");
      window.location.href = url;
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  async function fetchAppEmailUrl() {
    try {
      const url = await createInAppUrl(true);
      setAppEmailUrl(url);
      setStatus("");
    } catch (e) {
      setStatus("Failed to create in-app URL");
    }
  }

  function copyUrlToClipboard() {
    navigator.clipboard.writeText(appEmailUrl);
  }

  return {
    goToInApp: useCallbackRef(goToInApp),
    goToAppFree: useCallbackRef(goToAppFree),
    goToFreemium: useCallbackRef(goToFreemium),
    fetchAppEmailUrl: useCallbackRef(fetchAppEmailUrl),
    copyUrlToClipboard,
    isAndroid,
    appEmailUrl,
    toggleIsAndroid,
    countryCode,
    onCountryCodeChange,
  };
};
